import React, { memo, useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { CancelIcon, ImageIcon } from '@sharedCR/coreDesignSystem/icons';
import TextFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/TextFieldCDS';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import {
  cardStyles,
  previewImageStyles,
  previewWrapperStyles,
  removeButtonStyles,
  retakeButtonStyles,
} from './CaptureThumbnail.styles';

/**
 * One captured/selected document in the batch: preview, editable name,
 * remove, and (native only) retake.
 *
 * @param {Object} props
 * @param {Object} props.document - batch record { id, file, name, previewUrl }
 * @param {number} props.index - position in the batch (for accessible labels)
 * @param {Function} props.onRename - (id, name) => void
 * @param {Function} props.onRemove - (id) => void
 * @param {Function} [props.onRetake] - (id) => void; rendered only when provided (native)
 */
const CaptureThumbnail = memo(({ document, index, onRename, onRemove, onRetake }) => {
  const { id, name, previewUrl } = document;

  const onNameChange = useCallback(event => onRename(id, event.target.value), [id, onRename]);
  const onRemoveClick = useCallback(() => onRemove(id), [id, onRemove]);
  const onRetakeClick = useCallback(() => onRetake?.(id), [id, onRetake]);

  return (
    <Box data-testid={`capture-thumbnail-${index}`} sx={cardStyles}>
      <Box sx={previewWrapperStyles}>
        {previewUrl ? (
          <Box
            alt={name}
            component="img"
            data-testid={`capture-thumbnail-image-${index}`}
            src={previewUrl}
            sx={previewImageStyles}
          />
        ) : (
          <ImageIcon size={IconSize[40]} />
        )}
        <Box
          aria-label={`Remove document ${index + 1}`}
          component="button"
          data-testid={`capture-thumbnail-remove-${index}`}
          sx={removeButtonStyles}
          type="button"
          onClick={onRemoveClick}
        >
          <CancelIcon size={IconSize[16]} />
        </Box>
      </Box>
      <TextFieldCDS
        dataTestId={`capture-thumbnail-name-${index}`}
        name={`capture-thumbnail-name-${index}`}
        value={name}
        onChange={onNameChange}
      />
      {onRetake ? (
        <Button
          data-testid={`capture-thumbnail-retake-${index}`}
          size="small"
          sx={retakeButtonStyles}
          variant="text"
          onClick={onRetakeClick}
        >
          <Typography variant="medium14">Retake</Typography>
        </Button>
      ) : null}
    </Box>
  );
});

CaptureThumbnail.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    previewUrl: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onRename: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetake: PropTypes.func,
};

export default CaptureThumbnail;
